/* ==========================================================================
   RECENTLY VIEWED PRODUCTS
   Web Component that reads product IDs from localStorage and fetches
   product cards via Shopify's search + section rendering API.
   ========================================================================== */

const STORAGE_KEY = 'theme:recently-viewed-products';
const MAX_STORED = 20;

/* ── Track current product visit ─────────────────────────────────────────── */
const trackProductView = () => {
  const el = document.querySelector('[data-recently-viewed-track]');
  if (!el) return;

  try {
    const id = parseInt(el.dataset.recentlyViewedTrack, 10);
    let items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    items = items.filter((item) => item !== id);
    items.unshift(id);
    if (items.length > MAX_STORED) items.length = MAX_STORED;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Safari private mode — silently fail
  }
};

/* ── Get current product ID from page (for exclusion) ────────────────────── */
const getCurrentProductId = () => {
  const el = document.querySelector('[data-recently-viewed-track]');
  return el ? parseInt(el.dataset.recentlyViewedTrack, 10) : null;
};

/* ── Recently Viewed Products Component ──────────────────────────────────── */
class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    /* Wait for DOMContentLoaded so tracking runs first */
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    /* Track the current product first, then load */
    trackProductView();
    this.loadProducts();
  }

  getSearchQuery() {
    const excludeId = getCurrentProductId();
    const limit = parseInt(this.dataset.productsToShow, 10) || 4;

    try {
      let items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (excludeId) items = items.filter((id) => id !== excludeId);
      return items.slice(0, limit).map((id) => `id:${id}`).join(' OR ');
    } catch {
      return '';
    }
  }

  async loadProducts() {
    const query = this.getSearchQuery();
    if (!query) {
      this.closest('.shopify-section')?.remove();
      return;
    }

    const sectionId = this.dataset.sectionId;
    const url = `${window.Shopify.routes.root}search?type=product&q=${encodeURIComponent(query)}&section_id=${sectionId}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newContent = doc.querySelector('recently-viewed-products');

      if (newContent?.children.length > 0) {
        this.replaceChildren(...document.importNode(newContent, true).childNodes);
      } else {
        this.closest('.shopify-section')?.remove();
      }
    } catch {
      this.closest('.shopify-section')?.remove();
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
